/**
 * IndexedDB persistence for the *decompressed* canonical Sapling param
 * bytes (~50 MB total). This is the bucket the WASM prover reads from
 * on every send — it's expected to see the canonical bellman-format
 * bytes (uncompressed points + the 52,836-byte MPC trailer), exactly
 * the bytes the Zcash distribution hashes to the well-known SHA-256.
 *
 * Why IDB instead of Cache Storage: Cache Storage matches by URL and
 * is convenient for the wire format. But we also want a place that
 * survives Cache eviction (which iOS WebKit can do under storage
 * pressure) and that exposes raw bytes — so we keep the canonical
 * bytes here and treat Cache Storage purely as a compressed cache.
 *
 * If both layers are present, the worker decompresses once per device
 * lifetime; if IDB is wiped but Cache Storage survives, we just
 * re-decompress (no network); if both are wiped, we re-prefetch.
 */

import type { ParamKind } from "./hashes";

const DB_NAME = "telecrypt-params";
const DB_VERSION = 1;
const STORE = "canonical";

/**
 * Lazy singleton — every getter awaits the same open promise.
 */
let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
  return dbPromise;
}

function tx<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return getDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const store = t.objectStore(STORE);
        const req = fn(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error("IDB request failed"));
      }),
  );
}

export async function putCanonicalBytes(
  kind: ParamKind,
  bytes: Uint8Array,
): Promise<void> {
  // Wrap in a transferable-friendly ArrayBuffer slice so we don't pin
  // the worker's TypedArray view.
  const buf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  await tx<IDBValidKey>("readwrite", (store) => store.put(buf, kind));
}

export async function getCanonicalBytes(kind: ParamKind): Promise<Uint8Array | null> {
  const out = await tx<unknown>("readonly", (store) => store.get(kind));
  if (!out) return null;
  if (out instanceof ArrayBuffer) return new Uint8Array(out);
  // Some implementations hand back the Uint8Array directly; handle both.
  if (ArrayBuffer.isView(out)) {
    const v = out as ArrayBufferView;
    return new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
  }
  return null;
}

export async function hasCanonicalBytes(kind: ParamKind): Promise<boolean> {
  try {
    const out = await tx<IDBValidKey | undefined>(
      "readonly",
      (store) => store.getKey(kind) as IDBRequest<IDBValidKey | undefined>,
    );
    return out !== undefined && out !== null;
  } catch {
    return false;
  }
}

export async function deleteCanonicalBytes(kind: ParamKind): Promise<void> {
  await tx<undefined>("readwrite", (store) => store.delete(kind));
}
